import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PatientResponse as Patient } from '@emr/shared';

import { searchPatients } from '../api/patients.ts';
import { FailureNotice, LoadingNotice } from '../components/AsyncState.tsx';
import { Button, ErrorText, Field } from '../components/primitives.tsx';

/**
 * Search-and-select a patient by (last) name, for the booking form's `patientId` field.
 *
 * `patientSearchQuerySchema`'s own comment (`shared/src/clinicalRequests.ts`) says `name` matches a
 * `last_name` prefix via `patients_org_last_name_dob_idx` — the hint text below says that plainly
 * rather than leaving a front-desk user to discover it by trial and error.
 */
export function PatientPicker({
  selected,
  onSelect,
  onClear,
  error,
}: {
  readonly selected: Patient | null;
  readonly onSelect: (patient: Patient) => void;
  readonly onClear: () => void;
  readonly error?: string;
}) {
  const { t } = useTranslation('schedule');
  const [query, setQuery] = useState('');
  const trimmed = query.trim();
  const searchEnabled = trimmed.length >= 2;

  const results = useQuery({
    queryKey: ['patient-search', trimmed],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      searchPatients({ name: trimmed, limit: 10 }, signal),
    enabled: searchEnabled,
  });

  if (selected !== null) {
    return (
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-ink">{t('patientPicker.patientLabel')}</span>
        <div className="flex items-center justify-between gap-3 rounded-control border border-field-line bg-field px-3 py-2">
          <div className="min-w-0">
            <p className="m-0 truncate text-sm font-medium text-ink">
              {selected.lastName}, {selected.firstName}
            </p>
            <p className="m-0 truncate text-sm text-ink-muted">MRN {selected.mrn}</p>
          </div>
          <Button variant="secondary" onClick={onClear}>
            {t('patientPicker.change')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <Field
        label={t('patientPicker.findLabel')}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
        }}
        hint={t('patientPicker.findHint')}
        placeholder={t('patientPicker.findPlaceholder')}
      />
      {error !== undefined ? <ErrorText message={error} /> : null}

      {searchEnabled ? (
        results.data === undefined ? (
          results.isError ? (
            <FailureNotice error={results.error} />
          ) : (
            <LoadingNotice label={t('patientPicker.searching')} />
          )
        ) : results.data.length === 0 ? (
          <p className="m-0 text-sm text-ink-muted">{t('patientPicker.noMatches')}</p>
        ) : (
          <ul
            role="listbox"
            aria-label={t('patientPicker.matchingPatients')}
            className="m-0 flex max-h-48 flex-col gap-1 overflow-y-auto rounded-control border border-line bg-surface p-1"
          >
            {results.data.map((patient) => (
              <li key={patient.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => {
                    onSelect(patient);
                    setQuery('');
                  }}
                  className="w-full rounded-control px-2 py-1.5 text-left text-sm text-ink hover:bg-raised"
                >
                  {patient.lastName}, {patient.firstName}
                  <span className="ml-2 text-ink-muted">MRN {patient.mrn}</span>
                </button>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </div>
  );
}
